import type {
  AreaGeography,
  MapMetadataResponse,
  MetricRequest,
  MetricResponse,
  OverlayId,
} from "./domain";
import { householdWeightedMean } from "./presentation";

const AREA_OVERLAYS: Record<Exclude<AreaGeography, "base_area">, OverlayId> = {
  lsoa: "lsoas",
  msoa: "msoas",
  council_ward: "council_wards",
  county_division: "county_divisions",
  constituency: "constituencies",
  settlement: "settlements",
};

/** The area membership and percentile rules shared by hover details and rankings. */
export class AreaAnalysis {
  public readonly householdCounts: readonly (number | null)[];

  public constructor(
    private readonly kind: MetricRequest["kind"],
    private readonly metric: MetricResponse,
    private readonly metadata: MapMetadataResponse,
  ) {
    this.householdCounts = metric.operationalInputs?.map((row) => row.householdCount) ?? [];
  }

  public indices(geography: AreaGeography, index: number): readonly number[] {
    return geography === "base_area"
      ? [index]
      : (this.metadata.memberships[AREA_OVERLAYS[geography]][index] ?? []);
  }

  public percentile(geography: AreaGeography, index: number): number | null {
    const indices = this.indices(geography, index);
    if (this.kind === "statistic") {
      const baseIndex = indices[0];
      return indices.length === 1 && baseIndex !== undefined
        ? (this.metric.percentiles[baseIndex] ?? null)
        : null;
    }
    return householdWeightedMean(indices, this.metric.percentiles, this.householdCounts);
  }

  public medianAge(geography: AreaGeography, index: number): number | null {
    const ages = this.metadata.medianAges;
    return (
      (geography === "base_area" ? ages.baseAreas : ages.overlays[AREA_OVERLAYS[geography]])[
        index
      ] ?? null
    );
  }

  public percentiles(geography: AreaGeography): readonly (number | null)[] {
    const count =
      geography === "base_area"
        ? this.metadata.baseAreaMapIndices.length
        : this.metadata.memberships[AREA_OVERLAYS[geography]].length;
    return Array.from({ length: count }, (_, index) => this.percentile(geography, index));
  }
}
